import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconCreditCardFilled } from '@iwoca/icons';
import { Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { useGetPaymentCardData } from './useGetPaymentData';
import { TFundingRequirementsSteps } from '../../../funding/fundingSteps/orderFundingRequirements';
import { ONBOARDING_PATHS } from '../../constants';
import { CompletedStepItem } from '../onboardingChecks/onboardingStepItem/CompletedStepItem';
import { OnboardingStepItem } from '../onboardingChecks/onboardingStepItem/OnboardingStepItem';

export const AddPaymentMethod = ({
    step
}: {
    step: TFundingRequirementsSteps[0];
}) => {
    if (step.status === 'COMPLETED') {
        return <CompletedPaymentMethod />;
    }
    return (
        <OnboardingStepItem
            title={
                <FormattedMessage
                    id="_.credit_card.onboarding.link_debit_card_title"
                    defaultMessage="Set up your payments"
                />
            }
            link={ONBOARDING_PATHS.ONBOARDING_PAYMENT_METHOD}
        />
    );
};

const CompletedPaymentMethod = () => {
    const {
        lastDigits,
        expiryMonth,
        expiryYear,
        payment_amount,
        payment_preference
    } = useGetPaymentCardData();

    // Format card number with asterisks and last 4 digits
    const maskedCardNumber = lastDigits
        ? `**** **** **** ${lastDigits}`
        : '**** **** **** ****';

    // Format expiry date
    const formattedYear = expiryYear ? String(expiryYear).slice(-2) : '**';
    const expiryDate =
        expiryMonth && expiryYear ? `${expiryMonth}/${formattedYear}` : '**/**';

    const cardDetails = `${maskedCardNumber} ${expiryDate}`;

    const getTextForPaymentPreference = () => {
        switch (payment_preference) {
            case 'fixed_amount':
                return <FormattedCurrency value={payment_amount!} />;
            case 'minimum_amount':
                return (
                    <FormattedMessage
                        id="_.credit_card.onboarding.payment_method.payment_preference.minimum_amount"
                        defaultMessage="the minimum amount due"
                    />
                );
            case 'full_amount':
                return (
                    <FormattedMessage
                        id="_.credit_card.onboarding.payment_method.payment_preference.full_amount"
                        defaultMessage="the full statment balance"
                    />
                );

            default:
                return null;
        }
    };

    return (
        <CompletedStepItem
            editLink={ONBOARDING_PATHS.ONBOARDING_PAYMENT_METHOD}
        >
            <>
                <Text className="my-s text-m font-med text-primary-10">
                    <FormattedMessage
                        id="_.credit_card.onboarding.payment_method.completed.title"
                        defaultMessage="Your repayments"
                    />
                </Text>

                {/* Card details row */}
                <div className="mb-s mt-xs flex items-center gap-m">
                    <IconCreditCardFilled />
                    {/* Card number, expiry and CVV */}
                    <Text className="text-xs font-med text-structure-40">
                        {cardDetails}
                    </Text>
                </div>

                {payment_preference ? (
                    <Text className="text-xs font-med text-structure-40">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.completed.subtitle"
                            defaultMessage="Automatically pay {payment_preference} each month."
                            values={{
                                payment_preference:
                                    getTextForPaymentPreference()
                            }}
                        />
                    </Text>
                ) : (
                    <Text className="text-xs font-med text-error">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.not_set"
                            defaultMessage="Payment method not set."
                        />
                    </Text>
                )}
            </>
        </CompletedStepItem>
    );
};
